import React, { useEffect, useRef, useState } from "react";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { Download, Edit, Expand, Eye, EyeOff, Heart, ImageMinus, MoreHorizontal, Palette, RefreshCw, Share2, SmilePlus, Trash2, Trophy } from "lucide-react";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ImageDTO } from "@/types";
import CustomJoyride from "components/atoms/Joyride";
import { useTutorial } from "components/organisms/TutorialProvider";
import { controlsContainerTutorialSteps } from "utils/joyride/controlsContainerTutorialSteps";
import * as Styled from "./ControlsContainer.styled";
interface ControlsContainerProps {
  isFirstImage?: boolean;
  imageInfo: ImageDTO;
  handleToggle: (id: string) => void;
  handleShareClick: (e: React.MouseEvent, imageId?: string) => void;
  handleCompetitionsClick: (imageId?: string) => void;
  handleEditClick: (imageId?: string) => void;
  handleADetailerClick: (imageId?: string) => void;
  handleFaceSwapClick: (imageId?: string) => void;
  handleBGRemovalClick: (imageId?: string) => void;
  handleInpaintClick: (imageId?: string) => void;
  handleUpscaleClick: (imageId?: string) => void;
  prepareForDelete: (id: string) => void;
  handleVote: (id: string) => void;
  isUserImage: (username: string) => boolean;
}
const ControlsContainer: React.FC<ControlsContainerProps> = ({
  isFirstImage,
  imageInfo,
  handleToggle,
  handleShareClick,
  handleCompetitionsClick,
  handleADetailerClick,
  handleFaceSwapClick,
  handleBGRemovalClick,
  handleInpaintClick,
  handleUpscaleClick,
  prepareForDelete,
  handleVote,
  isUserImage
}) => {
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const popoverButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (tutorialState.imageViewer.completed && !tutorialState.controlsContainer.completed && !tutorialState.controlsContainer.started && isFirstImage) {
      startTutorial("controlsContainer");
    }
  }, [tutorialState, startTutorial, isFirstImage, tutorialStateInitialized]);
  useEffect(() => {
    if (tutorialState?.controlsContainer.started && !tutorialState.controlsContainer.completed && isFirstImage) {
      setRun(true);
    }
  }, [isFirstImage, tutorialState]);
  const handleControlsTutorialCallback = (data: {
    status: any;
    type: any;
    action: any;
    index: any;
  }) => {
    const {
      status,
      type,
      action,
      index
    } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      // Tutorial has finished or been skipped
      completeTutorial("controlsContainer");
      setRun(false);
      setStepIndex(0);
      const event = new MouseEvent("click", {
        bubbles: true
      });
      handleShareClick(event as unknown as React.MouseEvent<Element, MouseEvent>, imageInfo.id);
    } else if (type === EVENTS.STEP_BEFORE) {
      // Handle actions before each step
      const currentStep = controlsContainerTutorialSteps[index];
      if (currentStep.target === ".joyride-ellipsis-button" && action === ACTIONS.NEXT) {
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click();
        }
      }
      if (currentStep.target === ".joyride-heart-icon" && action === ACTIONS.NEXT) {
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click();
        }
      }
      // Add other actions for steps within ControlsContainer
    } else if (type === EVENTS.STEP_AFTER) {
      // Update the step index after each step
      if (action === ACTIONS.NEXT) {
        setStepIndex(prev => prev + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(prev => prev - 1);
      }
    }
  };
  return <Styled.Container className={isFirstImage ? "joyride-controls-container" : ""} data-sentry-element="unknown" data-sentry-component="ControlsContainer" data-sentry-source-file="index.tsx">
            <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                <CustomJoyride steps={controlsContainerTutorialSteps} run={run} stepIndex={stepIndex} disableScrolling callback={handleControlsTutorialCallback} data-sentry-element="CustomJoyride" data-sentry-source-file="index.tsx" />
                <Styled.ControlsContainer className={isFirstImage ? "joyride-controls-bar" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                        <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                            <Styled.ActionButton $isLiked={imageInfo.isVotedByUser} onClick={e => {
              e.stopPropagation();
              handleVote(imageInfo.id);
            }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Heart className={`h-4 w-4 mr-2 ${imageInfo.isVotedByUser ? "fill-rose-500" : ""}`} data-sentry-element="Heart" data-sentry-source-file="index.tsx" />
                                <span>{imageInfo.voteCount}</span>
                            </Styled.ActionButton>
                        </TooltipTrigger>
                        <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">
                            {imageInfo.isVotedByUser ? "Unlike this image" : "Like this image"}
                        </TooltipContent>
                    </Tooltip>

                    <Styled.ButtonContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                            <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                <Styled.IconButton aria-label="Share this image" onClick={e => handleShareClick(e, imageInfo.id)} className={isFirstImage ? "joyride-share-button" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <Share2 className="h-4 w-4 mr-2" data-sentry-element="Share2" data-sentry-source-file="index.tsx" />
                                </Styled.IconButton>
                            </TooltipTrigger>
                            <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">Share this image</TooltipContent>
                        </Tooltip>

                        {isUserImage(imageInfo.imageMetadata.username) && <Tooltip>
                                <TooltipTrigger asChild>
                                    <Styled.TogglePublicButton $isPublic={imageInfo.isPublic} onClick={e => {
                e.stopPropagation();
                handleToggle(imageInfo.id);
              }} className={isFirstImage ? "joyride-public-toggle" : ""}>
                                        {imageInfo.isPublic ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                                    </Styled.TogglePublicButton>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    <Styled.TooltipContentWrapper>
                                        <p>
                                            {imageInfo.isPublic ? "This image is visible to everyone" : "This image is only visible to you"}
                                        </p>
                                        <Styled.TooltipText>
                                            Click to{" "}
                                            {imageInfo.isPublic ? "make it private" : "make it public"}
                                        </Styled.TooltipText>
                                    </Styled.TooltipContentWrapper>
                                </TooltipContent>
                            </Tooltip>}

                        <Popover data-sentry-element="Popover" data-sentry-source-file="index.tsx">
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="index.tsx">
                                        <Styled.IconButton className={isFirstImage ? "joyride-ellipsis-button" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            <MoreHorizontal className="h-4 w-4 mr-2" data-sentry-element="MoreHorizontal" data-sentry-source-file="index.tsx" />
                                        </Styled.IconButton>
                                    </PopoverTrigger>
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">More Options</TooltipContent>
                            </Tooltip>

                            <Styled.PopoverContent data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <a href={imageInfo.image} download target="_blank" rel="noreferrer">
                                    <Styled.PopoverItem className={isFirstImage ? "joyride-download-option" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        <Download className="h-4 w-4 mr-2" data-sentry-element="Download" data-sentry-source-file="index.tsx" />
                                        <span>Download</span>
                                    </Styled.PopoverItem>
                                </a>
                                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleCompetitionsClick(imageInfo.id)} className={isFirstImage ? "joyride-competition-option" : ""}>
                                        <Trophy className="h-4 w-4 mr-2" />
                                        <span>Enter Competition</span>
                                    </Styled.PopoverItem>}
                                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleInpaintClick(imageInfo.id)}>
                                        <Edit className="h-4 w-4 mr-2" />
                                        <span>Edit</span>
                                    </Styled.PopoverItem>}
                                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => prepareForDelete(imageInfo.id)} className={isFirstImage ? "joyride-delete-option" : ""}>
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        <span>Delete</span>
                                    </Styled.PopoverItem>}
                                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleADetailerClick(imageInfo.id)} className={isFirstImage ? "joyride-refine-faces-option" : ""}>
                                        <SmilePlus className="h-4 w-4 mr-2" />
                                        <span>Refine Faces</span>
                                    </Styled.PopoverItem>}
                                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleFaceSwapClick(imageInfo.id)} className={isFirstImage ? "joyride-swap-faces-option" : ""}>
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        <span>Swap Faces</span>
                                    </Styled.PopoverItem>}
                                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleBGRemovalClick(imageInfo.id)} className={isFirstImage ? "joyride-remove-bg-option" : ""}>
                                        <ImageMinus className="h-4 w-4 mr-2" />
                                        <span>Remove BG</span>
                                    </Styled.PopoverItem>}
                                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleInpaintClick(imageInfo.id)} className={isFirstImage ? "joyride-spot-edit-option" : ""}>
                                        <Palette className="h-4 w-4 mr-2" />
                                        <span>Spot Edit</span>
                                    </Styled.PopoverItem>}
                                {isUserImage(imageInfo.imageMetadata.username) && <Styled.PopoverItem onClick={() => handleUpscaleClick(imageInfo.id)} className={isFirstImage ? "joyride-upscale-option" : ""}>
                                        <Expand className="h-4 w-4 mr-2" />
                                        <span>Upscale Image</span>
                                    </Styled.PopoverItem>}
                            </Styled.PopoverContent>
                        </Popover>
                    </Styled.ButtonContainer>
                </Styled.ControlsContainer>
                {isUserImage(imageInfo.imageMetadata.username) && <Styled.VisibilityMessage>
                        {imageInfo.isPublic ? "Visible to all" : "Only visible to you"}{" "}
                        - Tap eye icon to change
                    </Styled.VisibilityMessage>}
            </TooltipProvider>
        </Styled.Container>;
};
export default ControlsContainer;